import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { View, Text } from 'react-native';
import styled from 'styled-components/native';
import DataTable from '@preflighttech/preflight-tables';
import moment from 'moment';

import DataState from 'app/src/components/DataState';
import ResourceNotifications from 'app/src/components/Notifications/Resource';
import Print from 'app/src/elements/Print';
import minimalTable from 'app/src/elements/minimalTable';
import { Link } from 'app/src/utils/routing';
import currency from 'app/src/utils/currencyFormatter';
import {
  SALE_ASSET_CORE_FIELDS,
  OFFER_FIELDS
} from 'app/src/components/Sales/Process/queries';

const date = value => {
  if (!value) { return ''; }

  return moment(value).format('M/D/YY');
};

// Not defining in ./queries to avoid imports loop
const ASSET_SALE_ASSETS = gql`
  ${SALE_ASSET_CORE_FIELDS}
  ${OFFER_FIELDS}
  query AssetSaleAssets(
    $id: ID!
    $saleAssetId: ID
  ) {
    asset(id: $id) {
      sellerFeesTotal
      vin
      stockNumber
      saleAssets(id: $saleAssetId) {
        ...SaleAssetCoreFields
        soldAmount
        sale { id, name, saleOn }
        offers {
          ...OfferFields
          isOriginal
        }
        saleHistoryDeltas {
          id
          userDescription
          createdAt
          createdTimestamp
          saleHistoryDescription
        }
        saleHistoryComments {
          id
          userDescription
          createdAt
          createdTimestamp
          body
        }
      }
    }
  }
`;

const SaleAssetContainer = styled(View)`
  margin: 10px;
`;

const SaleName = styled(Text)`
  font-weight: 600;
`;

const Offers = styled(View)`
  margin: 5px;
  padding: 5px;
  background-color: white;
  border-width: 1px;
  border-color: blank;
`;

const Field = styled(View)`
  flex-direction: row;
  gap: 10px;
`;

const Label = styled(Text)`
  min-width: 100px;
`;

const columns = [
  {
    key: 'date',
    label: 'Date',
    sort: 'prevent',
    width: 90,
    content: ({ entry }) => moment(entry.createdAt).format('M/D/YYYY'),
  },
  {
    key: 'time',
    label: 'Time',
    sort: 'prevent',
    width: 90,
    content: ({ entry }) => moment(entry.createdAt).format('h:mm A'),
  },
  {
    key: 'userDescription',
    label: 'User',
    sort: 'prevent',
    width: "25%",
  },
  {
    key: 'deltaDescription',
    label: 'Action',
    sort: 'prevent',
  },
];

const SaleAsset = ({ asset, saleAsset }) => {
  const { sale, offers } = saleAsset;

  const saleName = sale ? sale.name : 'Sold Direct';

  const deltas = [];

  saleAsset.saleHistoryDeltas.forEach(delta => {
    const deltaObject = { ...delta };

    deltaObject.deltaDescription = deltaObject.saleHistoryDescription;

    if (deltaObject.deltaDescription) {
      deltas.push(deltaObject);
    }
  });

  saleAsset.saleHistoryComments.forEach(comment => {
    const { body, ...deltaObject } = comment;
    deltaObject.deltaDescription = `Comment: ${body}`;
    deltas.push(deltaObject);
  });

  const sortedDeltas = deltas
    .sort((a, b) => a.createdTimestamp - b.createdTimestamp);

  let releaseStatus;

  if (saleAsset.releaseConfirmed) {
    releaseStatus = 'Release Confirmed';
  } else if (saleAsset.release) {
    releaseStatus = 'Release Sent';
  }

  const sos = saleName.includes(' SOS ');

  return (
    <SaleAssetContainer>
      <Field>
        {sos ? (
          <SaleName>{asset.stockNumber}</SaleName>
        ) : (
          <Link to={`/sales/${sale.id}`}>
            <SaleName>{saleName}</SaleName>
          </Link>
        )}
        <ResourceNotifications resource={saleAsset} />
        <Print
          path={`reports/sale-history/${saleAsset.id}`}
          filename={`bid-history-${asset.vin}`}
        />
      </Field>
      <Field>
        <Label>Lot Number:</Label>
        <Text>{saleAsset.lotNumber}</Text>
      </Field>
      <Field>
        <Label>Status:</Label>
        <Text>
          {saleAsset.status}{' '}
          {saleAsset.soldAmount && currency(saleAsset.soldAmount)}
        </Text>
      </Field>
      <Field>
        <Label>ACH:</Label>
        <Text>
          Fees: {currency(asset.sellerFeesTotal)}
        </Text>
        {saleAsset.soldAmount && (
          <>
            <Text>Net: {currency(saleAsset.sellerNet)}</Text>
            <Text>
              Date of ACH: {date(saleAsset.sellerAchOn)}
            </Text>
          </>
        )}
      </Field>
      {releaseStatus && (
        <Text>{releaseStatus}</Text>
      )}
      {deltas.length > 0 && !sos && (
        <DataTable.Simple
          data={sortedDeltas}
          columns={columns}
          component={minimalTable}
          pageLength="All"
        />
      )}
    </SaleAssetContainer>
  );
};

const SaleHistory = ({ asset, saleAsset }) => {
  const { data, loading, error } = useQuery(ASSET_SALE_ASSETS, {
    variables: { id: asset.id, saleAssetId: saleAsset?.id }
  });

  if (!data) { return <DataState.Loading /> }

  const saleAssets = data.asset.saleAssets;

  if (0 === saleAssets.length) {
    return (<Text>Not listed in any sales</Text>);
  }

  return (
    <View>
      {saleAssets.map(saleAsset => (
        <SaleAsset
          key={saleAsset.id}
          saleAsset={saleAsset}
          asset={data.asset}
        />
      ))}
    </View>
  );
};

export default SaleHistory;
